import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import LoadingIndicator from "../../components/LoadingIndicator";
import OEContext from "../../layouts/OEContext";
import { axiosGet, axiosPost } from "../../data/helpers/baseApi";
import { SERVICE_URLS } from "../../data/helpers/serviceUrls";
import FlexContributionLimitLabel from "../../components/FlexContributionLimitLabel";
import { useQueryGetFlexContributionLimits } from "../../data/services/flexBenefitsService";

const yesNoOptions = ["Yes", "No"];
const contributionAmountTypeOptions = [
  { value: "Contribution", label: "Dollar amount" },
  { value: "Match", label: "Percentage" },
];
const currentPlanYear = `${new Date().getFullYear() + 1}`;
const nextYearJanuaryFirst = `${new Date().getFullYear() + 1}-01-01`;

const createBlankFormData = () => ({
  contributesToFSA: "",
  isJoiningPrestigeFSA: "",
  fsaJoinDate: nextYearJanuaryFirst,
  currentPlanEndDate: "",
  fsaPlanYear: currentPlanYear,
  plansAvailableForContribution: "",
  benefitGroupsEligible: "",
  matchOrContribution: "",
  annualEmployerContribution: "",
  isProRated: "",
  fsaNotes: "",
});

const createNoDataFormData = () => ({
  ...createBlankFormData(),
  fsaPlanYear: currentPlanYear,
});

const yesNoFromBool = (value) => {
  if (value === true) {
    return "Yes";
  }

  if (value === false) {
    return "No";
  }

  return "";
};

const normalizeOption = (value, allowedOptions) => {
  if (value === null || value === undefined) {
    return "";
  }

  const normalizedValue = `${value}`.trim();
  if (!normalizedValue) {
    return "";
  }

  return (
    allowedOptions.find((option) => option.toLowerCase() === normalizedValue.toLowerCase()) ?? ""
  );
};

const yesNoToBool = (value) => {
  if (value === "Yes") {
    return true;
  }

  if (value === "No") {
    return false;
  }

  return null;
};

const formatOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return `${value}`;
};

const parseOptionalNumber = (value) => {
  if (value === "") {
    return null;
  }

  return Number(value);
};

const formatDateForInput = (value) => {
  if (!value) {
    return "";
  }

  return new Date(value).toISOString().split("T")[0];
};

const parseOptionalDate = (value) => {
  if (!value) {
    return null;
  }

  return new Date(`${value}T00:00:00`);
};

const getSaveSuccessMessage = (benefitLabel) => `${benefitLabel} details were saved successfully.`;

const applyFsaJoinRule = (formData) => {
  if (formData.contributesToFSA === "Yes") {
    return {
      ...formData,
      isJoiningPrestigeFSA: "No",
      fsaJoinDate: nextYearJanuaryFirst,
      currentPlanEndDate: "",
    };
  }

  return formData;
};

const hydrateFormData = (data) =>
  applyFsaJoinRule({
    contributesToFSA: yesNoFromBool(data?.contributesToFSA),
    isJoiningPrestigeFSA: yesNoFromBool(data?.isJoiningPrestigeFSA),
    fsaJoinDate: formatDateForInput(data?.fsaJoinDate) || nextYearJanuaryFirst,
    currentPlanEndDate: formatDateForInput(data?.currentPlanEndDate),
    fsaPlanYear: currentPlanYear,
    plansAvailableForContribution: data?.plansAvailableForContribution ?? "",
    benefitGroupsEligible: data?.benefitGroupsEligible ?? "",
    matchOrContribution: normalizeOption(
      data?.matchOrContribution,
      contributionAmountTypeOptions.map((option) => option.value),
    ),
    annualEmployerContribution: formatOptionalNumber(data?.annualEmployerContribution),
    isProRated: yesNoFromBool(data?.isProRated),
    fsaNotes: data?.fsaNotes ?? "",
  });

const validateForm = (formData) => {
  const nextErrors = {};

  if (!formData.fsaPlanYear) {
    nextErrors.fsaPlanYear = "FSA plan year is required.";
  }

  if (
    formData.contributesToFSA === "Yes" &&
    !formData.annualEmployerContribution
  ) {
    nextErrors.annualEmployerContribution =
      "Contribution amount is required when the company contributes to the FSA.";
  }

  if (formData.contributesToFSA === "Yes" && !formData.matchOrContribution) {
    nextErrors.matchOrContribution =
      "Amount type is required when the company contributes to the FSA.";
  }

  if (formData.isJoiningPrestigeFSA === "Yes" && formData.contributesToFSA !== "Yes") {
    if (!formData.fsaJoinDate) {
      nextErrors.fsaJoinDate =
        "FSA join date is required when the client is joining Prestige FSA.";
    }
  }

  return nextErrors;
};

const pageStyle = {
  width: "100%",
  padding: "0 0 2rem",
  boxSizing: "border-box",
};

const contentStyle = {
  width: "100%",
  margin: 0,
  fontFamily: '"Segoe UI", Arial, sans-serif',
};

const formStyle = {
  width: "100%",
  padding: "20px 24px 24px",
  borderRadius: 0,
  border: "1px solid #d9d9d9",
  backgroundColor: "#fff",
  boxSizing: "border-box",
};

const sectionHeaderStyle = {
  marginBottom: 20,
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: 24,
  color: "#333",
};

const sectionSubtitleStyle = {
  margin: "0.4rem 0 0",
  fontSize: 14,
  color: "#666",
};

const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  gap: 20,
  alignItems: "start",
};

const sectionStyle = {
  width: "100%",
  padding: 0,
  boxSizing: "border-box",
};

const fullWidthSectionStyle = {
  ...sectionStyle,
  gridColumn: "1 / -1",
};

const contributionGroupStyle = {
  gridColumn: "1 / -1",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  gap: 20,
  padding: "18px 0 0",
  borderTop: "1px solid #e5e5e5",
  boxSizing: "border-box",
};

const contributionHeaderStyle = {
  gridColumn: "1 / -1",
};

const contributionTitleStyle = {
  margin: 0,
  fontSize: 18,
  color: "#333",
};

const contributionTextStyle = {
  margin: "0.4rem 0 0",
  fontSize: 14,
  color: "#666",
};

const labelStyle = {
  display: "block",
  fontWeight: 700,
  marginBottom: 8,
  color: "#333",
};

const controlStyle = {
  width: "100%",
  minHeight: 46,
  padding: "10px 12px",
  fontSize: 15,
  borderRadius: 4,
  border: "1px solid #c8c8c8",
  backgroundColor: "#fff",
  boxSizing: "border-box",
};

const notesStyle = {
  ...controlStyle,
  minHeight: 120,
  resize: "vertical",
};

const errorStyle = {
  marginBottom: 20,
  padding: 14,
  borderRadius: 4,
  border: "1px solid #d14343",
  backgroundColor: "#fff1f1",
  color: "#7a1010",
};

const successStyle = {
  marginBottom: 20,
  padding: 14,
  borderRadius: 4,
  border: "1px solid #2d8a39",
  backgroundColor: "#edf8ee",
  color: "#1f6f2b",
};

const metadataStyle = {
  fontSize: 12,
  color: "#888",
  marginBottom: "1rem",
};

const validationTextStyle = {
  color: "#b42318",
  fontSize: 13,
  fontWeight: 600,
  marginTop: 8,
};

const buttonRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 24,
};

const primaryButtonStyle = {
  backgroundColor: "#0078d4",
  color: "#fff",
  border: "none",
  padding: "12px 24px",
  fontSize: 15,
  fontWeight: 600,
  borderRadius: 4,
  cursor: "pointer",
};

const ClientFSAContribution = ({
  clientId: clientIdProp,
  oePeriodId: oePeriodIdProp,
  planOptions = [],
}) => {
  const routeParams = useParams();
  const clientId = clientIdProp ?? routeParams.clientId;
  const oePeriodId = oePeriodIdProp ?? routeParams.oePeriodId;
  const oePeriod = useContext(OEContext);

  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState(createBlankFormData());
  const [savedRecord, setSavedRecord] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const effectiveOePeriodId = oePeriod?.oePeriodId ?? oePeriodId;
  const { data: flexLimitsResponse } = useQueryGetFlexContributionLimits(
    effectiveOePeriodId,
  );
  const flexLimits = flexLimitsResponse?.data;

  useEffect(() => {
    let isMounted = true;

    const loadFsaData = async () => {
      setIsLoading(true);
      setSuccessMessage("");
      setErrorMessage("");

      try {
        const response = await axiosGet(
          SERVICE_URLS.CLIENT_FSA_GET(effectiveOePeriodId, clientId),
        );

        if (isMounted && response?.status === 200 && response.data) {
          // The FSA GET returns a templated 200 even when no row exists;
          // only treat as a real record when the PK is present.
          const isRealRecord = !!response.data.clientFSAId;
          setSavedRecord(isRealRecord ? response.data : null);
          setFormData(hydrateFormData(response.data));
        } else if (isMounted && response?.status === 204) {
          setSavedRecord(null);
          setFormData(createNoDataFormData());
        }
      } catch {
        if (isMounted) {
          setSavedRecord(null);
          setFormData(createBlankFormData());
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadFsaData();

    return () => {
      isMounted = false;
    };
  }, [clientId, effectiveOePeriodId]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      const nextFormData = {
        ...prev,
        [name]: value,
      };

      if (name === "contributesToFSA" && value !== "Yes") {
        nextFormData.matchOrContribution = "";
        nextFormData.annualEmployerContribution = "";
        nextFormData.isProRated = "";
        nextFormData.plansAvailableForContribution = "";
        nextFormData.benefitGroupsEligible = "";
      }

      if (name === "isJoiningPrestigeFSA" && value !== "Yes") {
        nextFormData.fsaJoinDate = nextYearJanuaryFirst;
        nextFormData.currentPlanEndDate = "";
      }

      return applyFsaJoinRule(nextFormData);
    });

    if (validationErrors[name] || (name === "contributesToFSA" && validationErrors.annualEmployerContribution)) {
      setValidationErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors[name];

        if (name === "contributesToFSA" && value !== "Yes") {
          delete nextErrors.annualEmployerContribution;
        }

        if (name === "contributesToFSA" && value === "Yes") {
          delete nextErrors.fsaJoinDate;
          delete nextErrors.currentPlanEndDate;
        }

        return nextErrors;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateForm(formData);
    setValidationErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    const numericOePeriodId = Number(effectiveOePeriodId);
    if (!Number.isFinite(numericOePeriodId)) {
      setErrorMessage("OE period could not be determined. Please reload the page.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axiosPost(SERVICE_URLS.CLIENT_FSA_UPSERT, {
        clientId,
        oePeriodId: numericOePeriodId,
        contributesToFSA: yesNoToBool(formData.contributesToFSA),
        isJoiningPrestigeFSA: formData.contributesToFSA === "Yes"
          ? false
          : yesNoToBool(formData.isJoiningPrestigeFSA),
        fsaJoinDate:
          formData.isJoiningPrestigeFSA === "Yes" && formData.contributesToFSA !== "Yes"
            ? parseOptionalDate(formData.fsaJoinDate)
            : null,
        currentPlanEndDate:
          formData.isJoiningPrestigeFSA === "Yes" && formData.contributesToFSA !== "Yes"
            ? parseOptionalDate(formData.currentPlanEndDate)
            : null,
        fsaPlanYear: currentPlanYear,
        plansAvailableForContribution:
          formData.contributesToFSA === "Yes" ? formData.plansAvailableForContribution || null : null,
        benefitGroupsEligible:
          formData.contributesToFSA === "Yes" ? formData.benefitGroupsEligible || null : null,
        matchOrContribution:
          formData.contributesToFSA === "Yes"
            ? normalizeOption(
                formData.matchOrContribution,
                contributionAmountTypeOptions.map((option) => option.value),
              ) || null
            : null,
        annualEmployerContribution:
          formData.contributesToFSA === "Yes"
            ? parseOptionalNumber(formData.annualEmployerContribution)
            : null,
        isProRated:
          formData.contributesToFSA === "Yes" ? yesNoToBool(formData.isProRated) : null,
        fsaNotes: formData.fsaNotes || null,
      });

      if ((response?.status === 200 || response?.status === 201) && response.data) {
        setSavedRecord(response.data);
        setFormData(hydrateFormData(response.data));
        setValidationErrors({});
        setSuccessMessage(getSaveSuccessMessage("FSA"));
      } else {
        setErrorMessage(
          "We couldn't save the FSA details right now. Please review the information and try again.",
        );
      }
    } catch {
      setErrorMessage(
        "We couldn't save the FSA details right now. Please review the information and try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderValidationError = (fieldName) => {
    if (!validationErrors[fieldName]) {
      return null;
    }

    return <div style={validationTextStyle}>{validationErrors[fieldName]}</div>;
  };

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div style={pageStyle}>
      <div style={contentStyle}>
        <div style={sectionHeaderStyle}>
          <div>
            <h2 style={sectionTitleStyle}>FSA - Flexible Spending Account</h2>
            <p style={{ ...sectionSubtitleStyle, width: 620 }}>
                  Use this section to confirm whether this client already has an FSA election or is newly joining Prestige FSA. Additional contribution and join fields appear only when needed.
            </p>
            <div>
              <FlexContributionLimitLabel label="FSA" amount={flexLimits?.fsa} />
            </div>

            <p>
            <div style={labelStyle}>FSA Contribution Awareness Notice</div>
            <p style={{ ...sectionSubtitleStyle, width: 620 }}>
              Employers may elect to offer and fund Flexible Spending Account (FSA) contributions 
              as part of their benefits strategy. FSA participation, eligibility, and contribution 
              limits are subject to applicable IRS regulations and plan document provisions. The 2026 
              IRS Health FSA contribution limit is $3,400. The 2027 contribution limit has not yet been 
              released and will be updated once guidance becomes available. Employer contributions count 
              toward the applicable IRS maximum and should be considered when establishing an FSA funding 
              strategy. In all cases, the applicable IRS regulations and plan documents will govern. If you 
              have questions regarding FSA participation or contribution elections, please contact your 
              dedicated PrestigePEO Benefits Specialist or Business Development Manager. This notice does not 
              constitute tax, legal, or financial advice and employers should consult a qualified advisor 
              regarding their individual circumstances.
            </p>
            </p>
          </div>
        </div>

        {successMessage ? <div data-testid="notification" style={successStyle}>{successMessage}</div> : null}
        {errorMessage ? <div data-testid="notification" style={errorStyle}>{errorMessage}</div> : null}

        {savedRecord?.lastUpdatedBy && (
          <p style={metadataStyle}>
            Last saved by {savedRecord.lastUpdatedBy}
            {savedRecord.lastUpdatedDate
              ? ` on ${new Date(savedRecord.lastUpdatedDate).toLocaleDateString()}`
              : ""}
          </p>
        )}

        <form onSubmit={handleSubmit} style={formStyle} data-testid="fsa-form">
          <div style={formGridStyle}>
            <div style={sectionStyle}>
              <label htmlFor="fsaPlanYear" style={labelStyle}>
                FSA plan year
              </label>
              <input
                type="text"
                id="fsaPlanYear"
                name="fsaPlanYear"
                data-testid="fsaPlanYear"
                value={formData.fsaPlanYear}
                onChange={handleChange}
                readOnly
                style={controlStyle}
              />
              {renderValidationError("fsaPlanYear")}
            </div>

            <div style={sectionStyle}>
              <label htmlFor="contributesToFSA" style={labelStyle}>
                Does this client currently have an employer-funded FSA election?
              </label>
              <select
                id="contributesToFSA"
                name="contributesToFSA"
                data-testid="contributesToFSA"
                value={formData.contributesToFSA}
                onChange={handleChange}
                style={controlStyle}
              >
                <option value="">Select...</option>
                {yesNoOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div style={sectionStyle}>
              <label htmlFor="isJoiningPrestigeFSA" style={labelStyle}>
                If you are a new client who did not join our FSA upon enrollment, would you like to join Prestige FSA for next calendar year?
              </label>
              <select
                id="isJoiningPrestigeFSA"
                name="isJoiningPrestigeFSA"
                data-testid="isJoiningPrestigeFSA"
                value={formData.isJoiningPrestigeFSA}
                onChange={handleChange}
                style={controlStyle}
                disabled={formData.contributesToFSA === "Yes"}
              >
                <option value="">Select...</option>
                {yesNoOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {formData.contributesToFSA === "Yes" && (
                <div style={validationTextStyle}>
                  If this client already has an existing FSA election, do not mark it as joining again.
                </div>
              )}
            </div>

            {formData.contributesToFSA === "Yes" && (
              <div style={contributionGroupStyle}>
                <div style={contributionHeaderStyle}>
                  <h4 style={contributionTitleStyle}>Employer contribution settings</h4>
                  <p style={contributionTextStyle}>
                    These fields appear only when the company contributes to the FSA.
                  </p>
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="matchOrContribution" style={labelStyle}>
                    Annual contribution amount type
                  </label>
                  <select
                    id="matchOrContribution"
                    name="matchOrContribution"
                    data-testid="matchOrContribution"
                    value={formData.matchOrContribution}
                    onChange={handleChange}
                    style={controlStyle}
                  >
                    <option value="">Select...</option>
                    {contributionAmountTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {renderValidationError("matchOrContribution")}
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="annualEmployerContribution" style={labelStyle}>
                    Annual employer contribution amount
                  </label>
                  <input
                    type="number"
                    id="annualEmployerContribution"
                    name="annualEmployerContribution"
                    data-testid="annualEmployerContribution"
                    value={formData.annualEmployerContribution}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="Enter amount"
                    style={controlStyle}
                  />
                  {renderValidationError("annualEmployerContribution")}
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="isProRated" style={labelStyle}>
                    Is the contribution amount pro-rated?
                  </label>
                  <select
                    id="isProRated"
                    name="isProRated"
                    data-testid="isProRated"
                    value={formData.isProRated}
                    onChange={handleChange}
                    style={controlStyle}
                  >
                    <option value="">Select...</option>
                    {yesNoOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="benefitGroupsEligible" style={labelStyle}>
                    Benefit groups eligible for contribution
                  </label>
                  <textarea
                    id="benefitGroupsEligible"
                    name="benefitGroupsEligible"
                    data-testid="benefitGroupsEligible"
                    value={formData.benefitGroupsEligible}
                    onChange={handleChange}
                    maxLength={255}
                    rows={3}
                    style={notesStyle}
                  />
                </div>
              </div>
            )}

            {formData.isJoiningPrestigeFSA === "Yes" && formData.contributesToFSA !== "Yes" && (
              <div style={contributionGroupStyle}>
                <div style={contributionHeaderStyle}>
                  <h4 style={contributionTitleStyle}>Prestige FSA join details</h4>
                  <p style={contributionTextStyle}>
                    These fields appear only when a group is joining Prestige FSA for the first time.
                  </p>
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="fsaJoinDate" style={labelStyle}>
                    FSA join date
                  </label>
                  <input
                    type="date"
                    id="fsaJoinDate"
                    name="fsaJoinDate"
                    data-testid="fsaJoinDate"
                    value={formData.fsaJoinDate}
                    onChange={handleChange}
                    disabled
                    style={controlStyle}
                  />
                  {renderValidationError("fsaJoinDate")}
                </div>
              </div>
            )}

            <div style={fullWidthSectionStyle}>
              <label htmlFor="fsaNotes" style={labelStyle}>
                FSA notes
              </label>
              <textarea
                id="fsaNotes"
                name="fsaNotes"
                data-testid="fsaNotes"
                value={formData.fsaNotes}
                onChange={handleChange}
                maxLength={255}
                rows={4}
                placeholder="Optional notes"
                style={notesStyle}
              />
            </div>
          </div>

          <div style={buttonRowStyle}>
            <button type="submit" style={primaryButtonStyle}>
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientFSAContribution;
